import React, { useEffect, useRef, useState } from 'react';
import { Share2, Link2, Check, MessageCircle, Mail, Smartphone, Facebook } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { canonicalUrl, copyLink, shareLink, shareTargets } from '@/lib/share';

interface Props {
  /** Localised path of the thing to share, e.g. path(`/jobs/${slug}`). */
  to: string;
  title: string;
  /** Short message placed before the link. */
  text?: string;
  variant?: 'solid' | 'outline' | 'icon';
  className?: string;
}

/**
 * SHARE — the recipient's link opens the exact item (project, lead, page),
 * never the home page or a redirect chain. Phones get the native share sheet
 * in one tap; desktops get a small menu with direct app links + Copy.
 */
const ShareButton: React.FC<Props> = ({ to, title, text, variant = 'outline', className = '' }) => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<'' | 'copied' | 'shared'>('');
  const ref = useRef<HTMLDivElement | null>(null);
  const url = canonicalUrl(to);
  const msg = text || title;
  const nativeShare = typeof navigator !== 'undefined' && 'share' in navigator;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const flash = (v: 'copied' | 'shared') => {
    setDone(v);
    window.setTimeout(() => setDone(''), 2200);
  };

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (nativeShare) {
      const r = await shareLink({ url, title, text: msg });
      if (r === 'copied' || r === 'shared') flash(r);
      return;
    }
    setOpen((o) => !o);
  };

  const copy = async () => {
    const r = await copyLink(url);
    if (r === 'copied') flash('copied');
    setOpen(false);
  };

  const label = done === 'copied' ? (es ? 'Enlace copiado' : 'Link copied') : done === 'shared' ? (es ? 'Compartido' : 'Shared') : es ? 'Compartir' : 'Share';
  const base =
    variant === 'solid'
      ? 'bg-slate-900 text-white hover:bg-slate-800'
      : variant === 'icon'
      ? 'h-9 w-9 justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-sm hover:bg-white'
      : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50';
  const t = shareTargets(url, msg);
  const item = 'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-100';

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={onClick}
        aria-label={es ? 'Compartir enlace' : 'Share link'}
        aria-expanded={open}
        className={`inline-flex items-center gap-2 font-bold transition-colors ${variant === 'icon' ? '' : 'rounded-xl px-4 py-2.5 text-sm'} ${base}`}
      >
        {done ? <Check className="h-4 w-4 text-emerald-500" /> : <Share2 className="h-4 w-4" />}
        {variant !== 'icon' && <span>{label}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl" role="menu">
          <a className={item} href={t.whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp
          </a>
          <a className={item} href={t.sms} onClick={() => setOpen(false)}>
            <Smartphone className="h-4 w-4 text-blue-600" /> {es ? 'Mensaje de texto' : 'Text message'}
          </a>
          <a className={item} href={t.email} onClick={() => setOpen(false)}>
            <Mail className="h-4 w-4 text-slate-600" /> Email
          </a>
          <a className={item} href={t.facebook} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            <Facebook className="h-4 w-4 text-blue-700" /> Facebook
          </a>
          <button type="button" className={item} onClick={copy}>
            <Link2 className="h-4 w-4 text-orange-500" /> {es ? 'Copiar enlace' : 'Copy link'}
          </button>
          <p className="truncate px-3 pb-1 pt-1.5 font-mono text-[10px] text-slate-400">{url}</p>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
